// Global packages and components
import { ImgHTMLAttributes } from 'react'
import { clsx } from 'clsx'
import { Roundal, Pill, ProductTags } from '~/components'

// Types
import type { RoundalProps } from '~/components/partials/common/roundal'

type ImgExtras = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'>

interface Props extends ImgExtras {
  src: string
  alt?: string
  tags?: RoundalProps[]
  tagsType?: 'roundal' | 'pill'
  showTagsMobile?: boolean
  adaptive?: boolean
  hasVariations?: boolean
  priority?: boolean
}

// Main export
const ProductImage = ({
  src,
  alt,
  tags,
  tagsType = 'roundal',
  showTagsMobile = false,
  adaptive = false,
  hasVariations = false,
  priority = false,
  className,
  ...imgProps
}: Props) => {
  // React 18 only passes fetchpriority through when it is lowercased
  const priorityProps = priority ? ({ fetchpriority: 'high' } as ImgExtras) : {}

  return (
    <div
      className={clsx(
        'relative flex items-center justify-center overflow-hidden rounded-lg bg-white',
        { 'h-full w-full': adaptive, 'aspect-square': !adaptive }
      )}
    >
      {tags && tags?.length > 0 && (
        <div
          className={clsx('absolute right-0 top-0 z-[1] -mx-0.5 p-1.5', {
            'hidden md:block': !showTagsMobile,
          })}
        >
          <ProductTags
            tags={tags}
            tagsType={tagsType}
          />
        </div>
      )}

      {hasVariations && (
        <div className="absolute bottom-0 left-0 p-1.5 md:p-2.5">
          <div className="p2-x z-[1] inline-block rounded-full border border-teal-green bg-white px-2 py-0.5 md:px-3 md:py-1">
            <p className="mb-0 text-2xs font-semibold md:text-xs">
              Variations
              <span className="hidden md:inline">&nbsp;available</span>
            </p>
          </div>
        </div>
      )}

      <img
        src={src}
        alt={alt ?? ''}
        className={clsx(
          'max-h-full w-auto max-w-full object-contain',
          className
        )}
        {...priorityProps}
        {...imgProps}
      />
    </div>
  )
}

export default ProductImage
